import { useCallback, useMemo, useRef, useState } from "react";
import { env } from "~/env";

type Status =
	| { key: "idle"; message?: undefined }
	| { key: "fetching"; message?: undefined }
	| { key: "success"; message: string }
	| { key: "error"; message: string };

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const isValidEmail = (value: string) => emailRegex.test(value);

const FIELD = "newsletter-email";

/**
 * Hook for handling newsletter signup
 * @returns value - the current value of the input
 * @returns setValue - function to set the value of the input
 * @returns handleSubmit - function to handle the submit of the form (includes validation)
 * @returns showInput - boolean to show or hide the input
 * @returns showLoading - boolean to show or hide the loading indicator
 * @returns message - messages that should be displayed to the user - if any
 */

export const useNewsletterSignup = (config: {
	missingEmailMessage?: string;
	invalidEmailMessage?: string;
	successMessage?: string;
	errorMessage?: string;
}) => {
	const [status, setStatus] = useState<Status>({ key: "idle" });
	const inputRef = useRef<HTMLInputElement>(null);

	const statuses = useMemo(
		() => ({
			idle: { key: "idle" } as const,
			fetching: { key: "fetching" } as const,
			invalid: {
				key: "error",
				message: config.invalidEmailMessage ?? "Vennligst skriv inn en gyldig e-postadresse.",
			} as const,
			missing: {
				key: "error",
				message: config.missingEmailMessage ?? "Vennligst skriv inn en e-postadresse.",
			} as const,
			success: {
				key: "success",
				message: config.successMessage ?? "Takk for at du meldte deg på nyhetsbrevet!",
			} as const,
			error: {
				key: "error",
				message: config.errorMessage ?? "Noe gikk galt. Vennligst prøv igjen!",
			} as const,
		}),
		[config],
	);

	const handleSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			setStatus({ key: "fetching" });

			// get email value from form and validate
			const value = (() => {
				const data = Object.fromEntries(new FormData(e.currentTarget));
				const val = data[FIELD].toString() ?? inputRef?.current?.value;

				if (!val) return setStatus(statuses.missing);
				if (!isValidEmail(val)) return setStatus(statuses.invalid);

				return val;
			})();
			if (!value) return;

			const res = await fetch(
				`${env.NEXT_PUBLIC_CENTRA_CHECKOUT}/newsletter-subscription/${value}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						email_field: value,
					}),
				},
			)
				.then((res) => {
					setStatus(statuses.success);
					if (inputRef?.current) inputRef.current.value = "";
					return res.json();
				})
				.catch((err) => {
					setStatus(statuses.error);
					return err;
				});
			return res;
		},
		[statuses],
	);

	const showInput = useMemo(() => status.key === "idle" || status.key === "error", [status]);

	const message = useMemo(() => {
		if (status.key === "success") {
			return {
				type: "success",
				label: config.successMessage ?? "Takk for at du meldte deg på nyhetsbrevet!",
			};
		}
		if (status.key === "error") {
			return {
				type: "error",
				label: config.errorMessage ?? "Noe gikk galt. Vennligst prøv igjen.",
			};
		}
		return undefined;
	}, [status, config]);

	const showLoading = useMemo(() => status.key === "fetching", [status]);

	const canSubmit = useMemo(() => status.key === "idle" || status.key === "error", [status]);

	return {
		inputName: FIELD,
		inputRef,
		handleSubmit,
		showInput,
		showLoading,
		message,
		canSubmit,
	};
};
